import { useState, useRef, useEffect, useCallback } from 'react';
import { getZone, isZoneUnlocked, ISLAND_SIZE } from '../data/zoneData';

/**
 * Custom hook for handling player movement in the game world
 * Handles WASD/Arrow key controls, collision detection, and zone locking
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.moveSpeed - Movement speed in pixels per frame (default: 48)
 * @param {boolean} options.disabled - Whether movement is disabled (e.g., when modals are open)
 * @param {number} options.currentStage - Current player stage (for zone locking)
 * @param {Array} options.worldObjects - Array of world objects for collision detection
 * @param {Function} options.onZoneLocked - Callback when player tries to enter locked zone
 * @returns {Object} Player movement state and controls
 */
export const usePlayerMovement = ({
  moveSpeed = 48,
  disabled = false,
  currentStage = 1,
  worldObjects = [],
  onZoneLocked = null,
}) => {
  // Player position and direction
  const [playerPos, setPlayerPos] = useState({ x: 0, y: 0 });
  const [direction, setDirection] = useState('down');
  const [isMoving, setIsMoving] = useState(false);
  
  // Refs for tracking pressed keys
  const keysPressed = useRef(new Set());
  
  // Collision detection - optimized to only check nearby objects
  const checkCollision = useCallback((newX, newY, objects) => {
    const playerRadius = 20;
    const checkRadius = 100; // Only check objects within 100 units
    
    for (const obj of objects) {
      if (!obj.collision) continue;
      
      // Quick distance check first (cheaper than sqrt)
      const dx = newX - obj.x;
      const dy = newY - obj.y;
      const distSquared = dx * dx + dy * dy;
      
      // Skip if too far away
      if (distSquared > checkRadius * checkRadius) continue;
      
      const objRadius = obj.collisionRadius || 20;
      const minDist = playerRadius + objRadius;
      
      if (distSquared < minDist * minDist) {
        return true; // Collision detected
      }
    }
    return false;
  }, []);
  
  // Check if player is trying to enter a locked zone
  const checkZoneLock = useCallback((newX, newY, stage) => {
    const zone = getZone(newX, newY);
    return !isZoneUnlocked(zone, stage);
  }, []);
  
  // Smooth WASD movement using requestAnimationFrame
  useEffect(() => {
    let animationFrameId = null;
    let lastTime = 0;
    
    const updateMovement = (currentTime) => {
      const keys = keysPressed.current;
      
      // Block movement when disabled
      if (keys.size === 0 || disabled) {
        setIsMoving(false);
        animationFrameId = requestAnimationFrame(updateMovement);
        return;
      }
      
      // Throttle to ~30fps (33ms between updates)
      if (currentTime - lastTime < 33) {
        animationFrameId = requestAnimationFrame(updateMovement);
        return;
      }
      lastTime = currentTime;
      
      let dx = 0, dy = 0;
      if (keys.has('w') || keys.has('arrowup')) { dy = -moveSpeed; }
      if (keys.has('s') || keys.has('arrowdown')) { dy = moveSpeed; }
      if (keys.has('a') || keys.has('arrowleft')) { dx = -moveSpeed; }
      if (keys.has('d') || keys.has('arrowright')) { dx = moveSpeed; }
      
      if (dx !== 0 || dy !== 0) {
        setIsMoving(true);
        
        // Set direction
        if (Math.abs(dx) > Math.abs(dy)) {
          setDirection(dx > 0 ? 'right' : 'left');
        } else {
          setDirection(dy > 0 ? 'down' : 'up');
        }
        
        // Calculate new position using functional update
        setPlayerPos(prev => {
          const newX = prev.x + dx;
          const newY = prev.y + dy;
          
          // Check world boundaries (square island)
          if (Math.abs(newX) > ISLAND_SIZE || Math.abs(newY) > ISLAND_SIZE) {
            return prev; // Can't go outside island
          }
          
          // Check zone lock
          const zone = getZone(newX, newY);
          if (!isZoneUnlocked(zone, currentStage)) {
            if (onZoneLocked) {
              onZoneLocked(zone);
            }
            return prev; // Can't enter locked zone
          }
          
          // Check collision with obstacles
          if (checkCollision(newX, newY, worldObjects)) {
            return prev; // Blocked by obstacle
          }
          
          return { x: newX, y: newY };
        });
      }
      
      animationFrameId = requestAnimationFrame(updateMovement);
    };
    
    const handleKeyDown = (e) => {
      if (disabled) return;
      
      const key = e.key.toLowerCase();
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(key)) {
        e.preventDefault();
        keysPressed.current.add(key);
      }
    };
    
    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      keysPressed.current.delete(key);
      if (keysPressed.current.size === 0) {
        setIsMoving(false);
      }
    };
    
    // Start the animation loop
    animationFrameId = requestAnimationFrame(updateMovement);
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [disabled, moveSpeed, currentStage, worldObjects, checkCollision, onZoneLocked]);
  
  // Reset player position
  const resetPosition = useCallback((x = 0, y = 0) => {
    setPlayerPos({ x, y });
    setDirection('down');
    setIsMoving(false);
    keysPressed.current.clear();
  }, []);
  
  // Teleport player to a specific position
  const teleportTo = useCallback((x, y) => {
    setPlayerPos({ x, y });
  }, []);
  
  return {
    playerPos,
    direction,
    isMoving,
    setPlayerPos,
    setDirection,
    setIsMoving,
    keysPressed,
    resetPosition,
    teleportTo,
  };
};

export default usePlayerMovement;
